// Libs
import classNames from 'classnames/bind';
import { Button, Flex, IconWarningSolid, Spinner, Text } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
// Others
import { QuizAnswersExportStatusEnum } from '~/utils/enum/common';
import { QUIZ_ANSWERS_EXPORT_NAMESPACE, QuizAnswersExportState } from './useQuizAnswersExport';
// Styles, images, icons
import styles from './QuizAnswersExportStatus.module.scss';

const cx = classNames.bind(styles);

const NAMESPACE = QUIZ_ANSWERS_EXPORT_NAMESPACE;

type Props = {
  exportState: QuizAnswersExportState;
};

/**
 * Status of the admin's quiz answers PDF, on its own row under the Exam Area buttons so the header
 * never gets crowded: progress, then "Scarica PDF" (the file is kept for one hour) or the error.
 */
const QuizAnswersExportStatus = (props: Props) => {
  //#region Destructuring Props
  const { job, isDownloading, downloadFile, getPhaseLabel, getErrorMessage } = props.exportState;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation([NAMESPACE]);
  //#endregion Declare Hook

  if (!job) return null;

  //#region Render
  return (
    <div className={cx('statusRow')}>
      <Text size='small' weight='bold'>
        {t('quiz_answers_export_status_label', {
          ns: NAMESPACE,
          name: job.filters.studentName || job.filters.studentId,
        })}
      </Text>

      {job.status === QuizAnswersExportStatusEnum.RUNNING && (
        <>
          <Spinner size='x-small' renderTitle={getPhaseLabel(job)} />
          <Text size='small'>{getPhaseLabel(job)}</Text>
        </>
      )}

      {job.status === QuizAnswersExportStatusEnum.DONE && (
        <>
          {!job.complete && (
            <>
              <IconWarningSolid color='warning' />
              <Text size='small' weight='bold' color='warning'>
                {t('quiz_answers_export_incomplete', { ns: NAMESPACE })}
              </Text>
            </>
          )}
          <Button color='primary' withBackground={false} type='button' disabled={isDownloading} onClick={downloadFile}>
            <Flex justifyItems='center' alignItems='center' gap='small'>
              <span>{t('quiz_answers_export_download_pdf', { ns: NAMESPACE })}</span>
              {isDownloading && <Spinner size='x-small' />}
            </Flex>
          </Button>
        </>
      )}

      {job.status === QuizAnswersExportStatusEnum.FAILED && (
        <>
          <IconWarningSolid color='error' />
          <Text size='small' color='danger'>
            {t('quiz_answers_export_failed', { ns: NAMESPACE })}: {getErrorMessage(job.error?.code)}
          </Text>
        </>
      )}
    </div>
  );
  //#endregion Render
};

export default QuizAnswersExportStatus;
